import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';

import { MarkdownEditor } from '@/components/markdown/MarkdownEditor';
import { Modal } from '@/components/Modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDeleteFile, useUploadFile } from '@/hooks/useFiles';
import { Risk } from '@/types';
import { cn } from '@/utils/classname';

import {
  useHackerOnePrograms,
  useHackerOneScopes,
  useHackerOneWeaknesses,
} from './HackerOneSubmitModal.queries';

interface HackerOneSubmitModalProps {
  open: boolean;
  onClose: () => void;
  vulnerabilities: Risk[];
  onSubmit: (params: HackerOneSubmitParams) => Promise<void>;
  programUrl?: string;
}

export interface HackerOneSubmitParams {
  teamHandle: string;
  title?: string;
  severityRating?: string;
  vulnerabilityInformation?: string;
  structuredScopeId?: string; // Maps to H1 Scope.id (string — API returns string IDs)
  weaknessId?: string; // Maps to H1 Weakness.id (string — API returns string IDs)
  attachmentS3Keys?: string[]; // NEW: Guard S3 keys for Report Intents flow
}

// ---------------------------------------------------------------------------
// Attachment upload state
// ---------------------------------------------------------------------------

interface AttachmentState {
  localId: string; // a uuid for react keying
  name: string; // original filename
  size: number;
  status: 'uploading' | 'complete' | 'error';
  progress: number; // 0-100
  s3Key?: string; // set once upload completes
  error?: string; // set if status === 'error'
}

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50 MiB

function sanitizeFileName(name: string): string {
  return name.replace(/[^A-Za-z0-9._-]/g, '_');
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const HACKERONE_REPORT_TEMPLATE = `## Summary\n\n\n## Steps to Reproduce / Proof of Concept\n\n\n## Remediation Steps\n\n\n## Supporting Materials / References\n\n\n## Impact\n\n`;

// ---------------------------------------------------------------------------
// Program combobox input (searchable typeahead)
// ---------------------------------------------------------------------------

interface ProgramComboboxProps {
  value: string;
  onChange: (handle: string) => void;
  disabled: boolean;
}

const ProgramCombobox: React.FC<ProgramComboboxProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const {
    data: programs,
    isPending: loadingPrograms,
    isError: programsError,
  } = useHackerOnePrograms(true);

  // The combobox tracks its own display string. The parent drives initial value
  // (e.g. from programUrl); local edits are tracked independently. When the
  // parent resets to '' (on modal close) the input will also read ''.
  // We use a half-controlled pattern: display = value when the user hasn't
  // typed yet or after a reset, otherwise the user's typed text is the display.
  const [localQuery, setLocalQuery] = useState(value);

  // When parent value changes to '' (reset), mirror it in local state.
  // This is a deliberate early-return state sync — accepted pattern for
  // resetting controlled inputs (avoids needing an effect).
  if (value === '' && localQuery !== '') {
    setLocalQuery('');
  }

  const [showSuggestions, setShowSuggestions] = useState(false);

  const filtered = useMemo(() => {
    if (!programs) return [];
    if (!localQuery) return programs.slice(0, 50);
    const lower = localQuery.toLowerCase();
    return programs
      .filter(
        p =>
          p.attributes.handle.toLowerCase().includes(lower) ||
          p.attributes.name.toLowerCase().includes(lower)
      )
      .slice(0, 50);
  }, [programs, localQuery]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setLocalQuery(v);
    // Do NOT call onChange here — only commit the typed value on blur or selection
    // to avoid triggering cascading scope/weakness queries on every keystroke.
    setShowSuggestions(true);
  };

  const handleSelect = (handle: string) => {
    setLocalQuery(handle);
    onChange(handle);
    setShowSuggestions(false);
  };

  const handleBlur = () => {
    // Commit the current typed value to the parent before hiding suggestions.
    onChange(localQuery);
    // Delay hiding to allow click on suggestion to fire first.
    setTimeout(() => setShowSuggestions(false), 150);
  };

  const inputClass = cn(
    'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded-md border px-3 py-2 text-sm',
    'focus:border-chariot-layer-primary focus:ring-chariot-layer-primary focus:ring-1 focus:outline-none',
    'disabled:cursor-not-allowed disabled:opacity-50'
  );

  // Show a spinner while programs are loading
  if (loadingPrograms) {
    return (
      <div className="relative">
        <input
          type="text"
          value=""
          disabled
          placeholder="Loading programs..."
          className={inputClass}
        />
        <span className="text-chariot-text-secondary absolute right-3 top-1/2 -translate-y-1/2 text-xs">
          Loading...
        </span>
      </div>
    );
  }

  // If fetch errored, fall back to a free-text input so the engineer is never blocked
  if (programsError) {
    return (
      <div className="space-y-1">
        <div
          className="bg-chariot-status-critical/10 text-chariot-status-critical rounded-md p-2 text-xs"
          role="alert"
        >
          Could not load programs list. Enter the handle manually below.
        </div>
        <input
          id="h1-team-handle"
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="e.g. acme-corp"
          disabled={disabled}
          className={inputClass}
        />
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        id="h1-team-handle"
        type="text"
        value={localQuery}
        onChange={handleInputChange}
        onFocus={() => setShowSuggestions(true)}
        onBlur={handleBlur}
        placeholder="Search by name or handle..."
        disabled={disabled}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={showSuggestions && filtered.length > 0}
        aria-haspopup="listbox"
        role="combobox"
        className={inputClass}
      />

      {showSuggestions && filtered.length > 0 && (
        <ul
          role="listbox"
          className={cn(
            'border-chariot-stroke bg-chariot-background-primary absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-md border shadow-md',
            'text-sm'
          )}
        >
          {filtered.map(program => (
            <li
              key={program.id}
              role="option"
              aria-selected={program.attributes.handle === value}
              onMouseDown={() => handleSelect(program.attributes.handle)}
              className={cn(
                'text-chariot-text-primary hover:bg-chariot-layer-primary/10 cursor-pointer px-3 py-2',
                program.attributes.handle === value &&
                  'bg-chariot-layer-primary/10'
              )}
            >
              <span className="font-medium">{program.attributes.handle}</span>
              {program.attributes.name &&
                program.attributes.name !== program.attributes.handle && (
                  <span className="text-chariot-text-secondary ml-2">
                    {program.attributes.name}
                  </span>
                )}
              {program.attributes.submission_state === 'disabled' && (
                <span className="text-chariot-status-critical ml-2 text-xs">
                  (closed)
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Parse a HackerOne team handle from a program URL.
 * Accepts both full URLs (https://hackerone.com/acme-corp) and bare handles.
 */
function parseHandleFromUrl(url: string): string {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    const segments = parsed.pathname.split('/').filter(Boolean);
    return segments[segments.length - 1] || '';
  } catch {
    // Not a URL — treat the whole string as the handle.
    return url;
  }
}

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

export const HackerOneSubmitModal: React.FC<HackerOneSubmitModalProps> = ({
  open,
  onClose,
  vulnerabilities,
  onSubmit,
  programUrl,
}) => {
  // Derive the initial teamHandle from programUrl.
  // The useState initializer handles the case where account data is already
  // available at mount time. The useEffect below handles the async case where
  // account data arrives after the modal is mounted.
  const [teamHandle, setTeamHandle] = useState<string>(() =>
    parseHandleFromUrl(programUrl ?? '')
  );
  const [title, setTitle] = useState('');
  const [severityRating, setSeverityRating] = useState('');
  const [vulnInfo, setVulnInfo] = useState(HACKERONE_REPORT_TEMPLATE);
  const [scopeId, setScopeId] = useState<string | undefined>(undefined);
  const [weaknessId, setWeaknessId] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Attachment upload state
  const [attachments, setAttachments] = useState<AttachmentState[]>([]);
  const [dropZoneError, setDropZoneError] = useState('');
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutateAsync: uploadFile } = useUploadFile();
  const { mutate: deleteFile } = useDeleteFile();

  // Reset cascading dropdowns whenever the program handle changes
  const handleTeamHandleChange = (handle: string) => {
    setTeamHandle(handle);
    setScopeId(undefined);
    setWeaknessId(undefined);
    if (error) setError('');
  };

  // Sync programUrl → teamHandle when account data loads asynchronously.
  // The modal can be mounted while closed (only gated by isPraetorianUser, not
  // by open), so the useState initializer runs when account data is still empty.
  // This effect re-runs whenever programUrl changes and fills in the handle only
  // when the modal is open and the user hasn't already typed/selected a handle.
  useEffect(() => {
    if (!open) return;
    if (teamHandle) return; // user already has a value — don't overwrite
    const parsed = parseHandleFromUrl(programUrl ?? '');
    if (parsed) {
      setTeamHandle(parsed);
    }
  }, [open, programUrl, teamHandle]);

  // Upload files dropped or selected by the user
  const processFiles = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      setDropZoneError('');

      // Size validation: reject files over 50 MiB
      const oversized = fileArray.filter(f => f.size > MAX_FILE_SIZE_BYTES);
      if (oversized.length > 0) {
        setDropZoneError(
          `${oversized.map(f => f.name).join(', ')} exceed${oversized.length === 1 ? 's' : ''} the 50 MiB size limit and will not be uploaded.`
        );
      }

      const validFiles = fileArray.filter(f => f.size <= MAX_FILE_SIZE_BYTES);

      for (const file of validFiles) {
        const localId = uuidv4();
        const s3Key = `hackerone-submissions/${Date.now()}-${sanitizeFileName(file.name)}`;

        // Push entry with uploading status
        setAttachments(prev => [
          ...prev,
          {
            localId,
            name: file.name,
            size: file.size,
            status: 'uploading',
            progress: 0,
          },
        ]);

        try {
          const arrayBuffer = await file.arrayBuffer();
          await uploadFile({
            name: s3Key,
            content: arrayBuffer,
            ignoreSnackbar: true,
            onProgress: ev => {
              setAttachments(prev =>
                prev.map(a =>
                  a.localId === localId
                    ? { ...a, progress: Math.round(ev.percent) }
                    : a
                )
              );
            },
          });
          // Upload complete
          setAttachments(prev =>
            prev.map(a =>
              a.localId === localId
                ? { ...a, status: 'complete', progress: 100, s3Key }
                : a
            )
          );
        } catch (err) {
          setAttachments(prev =>
            prev.map(a =>
              a.localId === localId
                ? { ...a, status: 'error', error: String(err) }
                : a
            )
          );
        }
      }
    },
    [uploadFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDraggingOver(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        processFiles(e.dataTransfer.files);
      }
    },
    [processFiles]
  );

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = () => {
    setIsDraggingOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
      // Reset input so re-selecting same file triggers onChange
      e.target.value = '';
    }
  };

  const handleRemoveAttachment = (localId: string) => {
    const attachment = attachments.find(a => a.localId === localId);
    if (attachment?.status === 'complete' && attachment.s3Key) {
      // Best-effort cleanup from Guard S3 — ignore errors
      deleteFile({ name: attachment.s3Key });
    }
    setAttachments(prev => prev.filter(a => a.localId !== localId));
  };

  const hasUploading = attachments.some(a => a.status === 'uploading');

  // Cascading data
  const { data: scopes, isPending: loadingScopes } = useHackerOneScopes(
    teamHandle.trim()
  );

  const { data: weaknesses, isPending: loadingWeaknesses } =
    useHackerOneWeaknesses(teamHandle.trim());

  const eligibleScopes = useMemo(
    () => (scopes ?? []).filter(s => s.attributes.eligible_for_submission),
    [scopes]
  );

  const handleSubmit = async () => {
    if (!teamHandle.trim()) {
      setError('HackerOne program handle is required');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      const s3Keys = attachments
        .filter(a => a.status === 'complete' && a.s3Key)
        .map(a => a.s3Key!);

      await onSubmit({
        teamHandle: teamHandle.trim(),
        title: title.trim() || undefined,
        severityRating: severityRating || undefined,
        vulnerabilityInformation: vulnInfo.trim() || undefined,
        structuredScopeId: scopeId,
        weaknessId: weaknessId,
        attachmentS3Keys: s3Keys.length > 0 ? s3Keys : undefined,
      });
      setSuccess(
        `Submitted vulnerability to HackerOne program "${teamHandle}".`
      );
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      const axiosErr = err as { message?: string };
      setError(axiosErr?.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      // Clean up any completed uploads that weren't consumed by a successful
      // submission. Fire-and-forget — don't block the close on network calls.
      if (!success) {
        attachments.forEach(entry => {
          if (entry.status === 'complete' && entry.s3Key) {
            deleteFile({ name: entry.s3Key });
          }
        });
      }

      setTeamHandle('');
      setTitle('');
      setSeverityRating('');
      setVulnInfo('');
      setScopeId(undefined);
      setWeaknessId(undefined);
      setError('');
      setSuccess('');
      setAttachments([]);
      setDropZoneError('');
      setIsDraggingOver(false);
      onClose();
    }
  };

  if (!open) return null;

  const handleIsEmpty = !teamHandle.trim();

  return (
    <Modal
      size="xl"
      title="Submit to HackerOne"
      subtitle="Submit this vulnerability to a HackerOne bug bounty program"
      open={open}
      onClose={handleClose}
      disableAutoFocus
      footer={{
        text: isSubmitting ? 'Submitting...' : 'Submit Report',
        onClick: handleSubmit,
        disabled:
          !teamHandle.trim() || isSubmitting || !!success || hasUploading,
      }}
    >
      <div className="space-y-4">
        {error && (
          <div
            className="bg-chariot-status-critical/10 text-chariot-status-critical rounded-md p-2 text-sm"
            role="alert"
          >
            {error}
          </div>
        )}

        {success && (
          <div
            className="bg-chariot-status-positive/10 text-chariot-status-positive rounded-md p-2 text-sm"
            role="status"
          >
            {success}
          </div>
        )}

        {/* Program Handle — searchable typeahead combobox */}
        <div>
          <label
            htmlFor="h1-team-handle"
            className="text-chariot-text-primary mb-1 block text-sm font-medium"
          >
            Program Handle{' '}
            <span className="text-chariot-status-critical">*</span>
          </label>
          <ProgramCombobox
            value={teamHandle}
            onChange={handleTeamHandleChange}
            disabled={isSubmitting || !!success}
          />
          <p className="text-chariot-text-secondary mt-1 text-xs">
            The program handle from the HackerOne URL, e.g. for
            hackerone.com/acme-corp enter acme-corp
          </p>
        </div>

        {/* Title Override */}
        <div>
          <label
            htmlFor="h1-title"
            className="text-chariot-text-primary mb-1 block text-sm font-medium"
          >
            Title Override
          </label>
          <input
            id="h1-title"
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Leave blank to auto-populate from finding"
            disabled={isSubmitting || !!success}
            className={cn(
              'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded-md border px-3 py-2 text-sm',
              'focus:border-chariot-layer-primary focus:ring-chariot-layer-primary focus:outline-none focus:ring-1',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
          />
        </div>

        {/* Vulnerability Information */}
        <div>
          <label className="text-chariot-text-primary mb-1 block text-sm font-medium">
            Vulnerability Information
          </label>
          <div className="border-chariot-stroke bg-chariot-background-primary h-[300px] rounded-lg border">
            <MarkdownEditor
              value={vulnInfo}
              onChange={value => setVulnInfo(value || '')}
              preview="edit"
              className="h-full"
            />
          </div>
          <p className="text-chariot-text-secondary mt-1 text-xs">
            Detailed description of the vulnerability for the HackerOne report
          </p>
        </div>

        {/* Severity Override */}
        <div>
          <label
            htmlFor="h1-severity"
            className="text-chariot-text-primary mb-1 block text-sm font-medium"
          >
            Severity Override
          </label>
          <Select
            value={severityRating || '__auto__'}
            onValueChange={v => setSeverityRating(v === '__auto__' ? '' : v)}
            disabled={isSubmitting || !!success}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__auto__">Auto-detect from finding</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="none">None</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Asset / Scope (cascading, optional) */}
        <div>
          <label
            htmlFor="h1-scope"
            className="text-chariot-text-primary mb-1 block text-sm font-medium"
          >
            Asset / Scope
          </label>
          <Select
            value={scopeId ?? ''}
            onValueChange={v => setScopeId(v || undefined)}
            disabled={
              handleIsEmpty || loadingScopes || isSubmitting || !!success
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue
                placeholder={
                  handleIsEmpty
                    ? 'Select a program first'
                    : loadingScopes
                      ? 'Loading scopes...'
                      : eligibleScopes.length === 0
                        ? 'No eligible scopes'
                        : 'Select an asset (optional)'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {eligibleScopes.map(scope => (
                <SelectItem key={scope.id} value={scope.id}>
                  {scope.attributes.asset_type}:{' '}
                  {scope.attributes.asset_identifier}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-chariot-text-secondary mt-1 text-xs">
            Leave blank to let HackerOne infer from the program defaults
          </p>
        </div>

        {/* Weakness / CWE (cascading, optional) */}
        <div>
          <label
            htmlFor="h1-weakness"
            className="text-chariot-text-primary mb-1 block text-sm font-medium"
          >
            Weakness (CWE)
          </label>
          <Select
            value={weaknessId ?? ''}
            onValueChange={v => setWeaknessId(v || undefined)}
            disabled={
              handleIsEmpty || loadingWeaknesses || isSubmitting || !!success
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue
                placeholder={
                  handleIsEmpty
                    ? 'Select a program first'
                    : loadingWeaknesses
                      ? 'Loading weaknesses...'
                      : 'Select a weakness (optional)'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {(weaknesses ?? []).map(w => (
                <SelectItem key={w.id} value={w.id}>
                  {w.attributes.name}
                  {w.attributes.external_id
                    ? ` (${w.attributes.external_id})`
                    : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Attachments */}
        <div>
          <label className="text-chariot-text-primary mb-1 block text-sm font-medium">
            Attachments
          </label>
          <p className="text-chariot-text-secondary mb-2 text-xs">
            Screenshots, videos, or logs of the vulnerability (optional).
            Uploaded files are relayed to HackerOne at submission time.
          </p>

          {/* Drop zone */}
          <div
            role="button"
            tabIndex={0}
            aria-label="Upload attachments"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            className={cn(
              'border-chariot-stroke flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed px-4 py-6 transition-colors',
              isDraggingOver
                ? 'border-chariot-layer-primary bg-chariot-layer-primary/5'
                : 'hover:border-chariot-layer-primary hover:bg-chariot-layer-primary/5',
              'focus:outline-none focus:ring-2 focus:ring-offset-2'
            )}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileInputChange}
              tabIndex={-1}
            />
            <svg
              className="text-chariot-text-secondary mb-2 h-8 w-8"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 16V4m0 0L8 8m4-4 4 4M4 20h16"
              />
            </svg>
            <p className="text-chariot-text-secondary text-sm">
              Drag &amp; drop files here, or{' '}
              <span className="text-chariot-layer-primary font-medium">
                click to select
              </span>
            </p>
            <p className="text-chariot-text-secondary mt-1 text-xs">
              Any file type &middot; Max 50 MiB per file
            </p>
          </div>

          {/* Size validation error */}
          {dropZoneError && (
            <p
              className="text-chariot-status-critical mt-1 text-xs"
              role="alert"
            >
              {dropZoneError}
            </p>
          )}

          {/* Attachment list */}
          {attachments.length > 0 && (
            <ul className="mt-3 space-y-2">
              {attachments.map(attachment => (
                <li
                  key={attachment.localId}
                  className="border-chariot-stroke bg-chariot-background-primary flex items-center gap-3 rounded-md border px-3 py-2"
                >
                  {/* File info */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-chariot-text-primary truncate text-sm font-medium">
                        {attachment.name}
                      </span>
                      <span className="text-chariot-text-secondary shrink-0 text-xs">
                        {formatFileSize(attachment.size)}
                      </span>
                    </div>

                    {/* Progress bar while uploading */}
                    {attachment.status === 'uploading' && (
                      <div className="mt-1">
                        <div className="bg-chariot-stroke h-1 w-full overflow-hidden rounded-full">
                          <div
                            className="bg-chariot-layer-primary h-1 rounded-full transition-all duration-200"
                            style={{ width: `${attachment.progress}%` }}
                            role="progressbar"
                            aria-valuenow={attachment.progress}
                            aria-valuemin={0}
                            aria-valuemax={100}
                          />
                        </div>
                        <p className="text-chariot-text-secondary mt-0.5 text-xs">
                          {attachment.progress}%
                        </p>
                      </div>
                    )}

                    {/* Error message */}
                    {attachment.status === 'error' && (
                      <p
                        className="text-chariot-status-critical mt-0.5 text-xs"
                        role="alert"
                      >
                        {attachment.error || 'Upload failed'}
                      </p>
                    )}
                  </div>

                  {/* Status icon */}
                  <div className="shrink-0">
                    {attachment.status === 'uploading' && (
                      <svg
                        className="text-chariot-text-secondary h-4 w-4 animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        aria-label="Uploading"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        />
                      </svg>
                    )}
                    {attachment.status === 'complete' && (
                      <svg
                        className="text-chariot-status-positive h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-label="Upload complete"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                    {attachment.status === 'error' && (
                      <svg
                        className="text-chariot-status-critical h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-label="Upload failed"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    )}
                  </div>

                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={() => handleRemoveAttachment(attachment.localId)}
                    aria-label={`Remove ${attachment.name}`}
                    className={cn(
                      'text-chariot-text-secondary hover:text-chariot-text-primary shrink-0 rounded p-0.5 transition-colors',
                      'focus:outline-none focus:ring-1'
                    )}
                  >
                    <svg
                      className="h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Vulnerabilities list */}
        {vulnerabilities.length > 0 && (
          <div className="border-chariot-stroke rounded-md border p-3">
            <p className="text-chariot-text-secondary mb-2 text-xs font-medium uppercase">
              Vulnerabilities to Submit
            </p>
            <ul className="space-y-1">
              {vulnerabilities.slice(0, 5).map(v => (
                <li key={v.key} className="text-chariot-text-primary text-sm">
                  {v.name}
                </li>
              ))}
              {vulnerabilities.length > 5 && (
                <li className="text-chariot-text-secondary text-sm">
                  ...and {vulnerabilities.length - 5} more
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
};
